import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { integrations, tenants } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ConnectionStatus = "connected" | "disconnected" | "loading" | "error";

interface ChannelState {
  slack: ConnectionStatus;
  google: ConnectionStatus;
}

// ---------------------------------------------------------------------------
// Route
//
// Registered at /onboarding/channels. Search params:
//   ?google=connected | ?google_error=<reason>
//   ?slack=connected  | ?slack_error=<reason>
// The backend OAuth callbacks redirect here (instead of /settings/integrations)
// when /connect was started with ?return=onboarding.
// ---------------------------------------------------------------------------

type SearchParams = {
  google?: string;
  google_error?: string;
  slack?: string;
  slack_error?: string;
};

export const Route = createFileRoute("/_authed/onboarding/channels")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    google: search.google as string | undefined,
    google_error: search.google_error as string | undefined,
    slack: search.slack as string | undefined,
    slack_error: search.slack_error as string | undefined,
  }),
  component: ConnectChannelsPage,
});
// useSearch from: "/_authed/onboarding/channels"
// navigate to: "/_authed/onboarding/language"

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ConnectChannelsPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authed/onboarding/channels" });
  const tenantsList = useAuthStore((s) => s.tenants);
  const tenant = tenantsList[0] ?? null;

  const [channels, setChannels] = useState<ChannelState>({
    slack: "loading",
    google: "loading",
  });
  const [connecting, setConnecting] = useState<"slack" | "google" | null>(null);

  // -------------------------------------------------------------------------
  // On mount: load current integration status, then apply any OAuth callback
  // query params that the backend appended after the round-trip.
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      integrations.google.status().then((s) => s.connected).catch(() => false),
      tenant
        ? integrations.slack.status(tenant.id).then((s) => s.connected).catch(() => false)
        : Promise.resolve(false),
    ]).then(([google, slack]) => {
      if (cancelled) return;
      setChannels({
        slack: slack ? "connected" : "disconnected",
        google: google ? "connected" : "disconnected",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [tenant?.id]);

  useEffect(() => {
    // Backend redirects to /settings/integrations?google=connected (or error).
    // Opsi A: we stored "onboarding_return=channels" in sessionStorage before
    // redirecting, so /settings/integrations can bounce back here. By the time
    // we're on this page the query params carry the result.
    if (search.google === "connected") {
      setChannels((prev) => ({ ...prev, google: "connected" }));
    } else if (search.google_error) {
      setChannels((prev) => ({ ...prev, google: "error" }));
    }

    if (search.slack === "connected") {
      setChannels((prev) => ({ ...prev, slack: "connected" }));
    } else if (search.slack_error) {
      setChannels((prev) => ({ ...prev, slack: "error" }));
    }
  }, [search]);

  // -------------------------------------------------------------------------
  // OAuth connect handlers
  // -------------------------------------------------------------------------

  const handleConnect = async (provider: "slack" | "google") => {
    if (provider === "slack" && !tenant) return;
    setConnecting(provider);
    try {
      // The backend remembers `return=onboarding` in a cookie and redirects the
      // OAuth callback back to this step.
      const { authorization_url } =
        provider === "google"
          ? await integrations.google.connect("onboarding")
          : await integrations.slack.install(tenant!.id, "onboarding");
      window.location.href = authorization_url;
    } catch {
      setChannels((prev) => ({ ...prev, [provider]: "error" }));
      setConnecting(null);
    }
  };

  // -------------------------------------------------------------------------
  // Advance to step 3
  // -------------------------------------------------------------------------

  const handleNext = async () => {
    if (tenant) {
      await tenants.updateOnboardingStep(tenant.id, 3).catch(() => {});
      // Refresh the store so _authed's step-guard sees the new step and
      // doesn't bounce us back here.
      await useAuthStore.getState().refresh();
    }
    void navigate({ to: "/onboarding/language" });
  };

  const isLoading = channels.slack === "loading" || channels.google === "loading";

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <PageShell>
      <StepIndicator current={2} total={4} />

      <div className="mt-6">
        <h1 className="font-semibold tracking-tight text-3xl text-[color:var(--color-abyssal)]">
          Connect your channels.
        </h1>
        <p className="mt-2 text-[color:var(--color-abyssal)]/60">
          Summaries will be delivered here after each meeting. You can always add more later.
        </p>
      </div>

      <div className="mt-7 space-y-3">
        {/* Email — always on, no action needed */}
        <ChannelCard
          icon={<EmailIcon />}
          name="Email"
          description="Summaries sent to the meeting owner's inbox via Infobip."
          status="connected"
          badge="Always on"
        />

        {/* Slack */}
        <ChannelCard
          icon={<SlackIcon />}
          name="Slack"
          description="Post a summary to your workspace's default channel."
          status={channels.slack}
          onConnect={() => handleConnect("slack")}
          connecting={connecting === "slack"}
          errorHint={
            search.slack_error
              ? slackErrorHint(search.slack_error)
              : undefined
          }
        />

        {/* Google Docs */}
        <ChannelCard
          icon={<GoogleDocsIcon />}
          name="Google Docs"
          description="Create a Doc on the meeting owner's Drive for each summary."
          status={channels.google}
          onConnect={() => handleConnect("google")}
          connecting={connecting === "google"}
          errorHint={
            search.google_error
              ? googleErrorHint(search.google_error)
              : undefined
          }
        />
      </div>

      {/* Actions */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={handleNext}
          className="text-sm text-[color:var(--color-abyssal)]/50 hover:text-[color:var(--color-abyssal)]/80 transition-colors"
        >
          Skip for now
        </button>
        <button
          onClick={handleNext}
          disabled={isLoading}
          className="rounded-md bg-[color:var(--color-north-star)] px-5 py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// ChannelCard
// ---------------------------------------------------------------------------

interface ChannelCardProps {
  icon: React.ReactNode;
  name: string;
  description: string;
  status: ConnectionStatus;
  badge?: string;
  onConnect?: () => void;
  connecting?: boolean;
  errorHint?: string;
}

function ChannelCard({
  icon,
  name,
  description,
  status,
  badge,
  onConnect,
  connecting,
  errorHint,
}: ChannelCardProps) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-sm">
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[color:var(--color-abyssal)]/[0.04]">
          {icon}
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-[color:var(--color-abyssal)]">{name}</span>
            {badge ? (
              <span className="rounded-full bg-[color:var(--color-north-star)]/10 px-2 py-0.5 text-xs font-medium text-[color:var(--color-north-star)]">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-[color:var(--color-abyssal)]/50 leading-snug">
            {description}
          </p>
          {errorHint ? (
            <p className="mt-1 text-xs text-[color:var(--color-coral)]">{errorHint}</p>
          ) : null}
        </div>

        {/* Status / Action */}
        <div className="flex-shrink-0">
          {status === "loading" ? (
            <Spinner small />
          ) : status === "connected" ? (
            <ConnectedBadge />
          ) : (
            <button
              onClick={onConnect}
              disabled={connecting}
              className="rounded-md border border-[color:var(--color-abyssal)]/15 px-3 py-1.5 text-xs font-medium text-[color:var(--color-abyssal)] transition-colors hover:border-[color:var(--color-north-star)] hover:text-[color:var(--color-north-star)] disabled:opacity-40"
            >
              {connecting ? (
                <span className="flex items-center gap-1.5">
                  <Spinner small />
                  Opening…
                </span>
              ) : status === "error" ? (
                "Retry"
              ) : (
                "Connect →"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectedBadge() {
  return (
    <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M2 5l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Connected
    </span>
  );
}

// ---------------------------------------------------------------------------
// Error hint helpers
// ---------------------------------------------------------------------------

function googleErrorHint(error: string): string {
  if (error === "session_expired") return "Your session expired. Please try again.";
  if (error === "access_denied") return "Access was denied. Please try again and approve the permissions.";
  return "Connection failed. Please try again.";
}

function slackErrorHint(error: string): string {
  if (error === "session_expired") return "Your session expired. Please try again.";
  if (error === "access_denied") return "Access was denied. Please try again and approve the permissions.";
  return "Connection failed. Please try again.";
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function EmailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-[color:var(--color-abyssal)]/60">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 7l10 7 10-7" strokeLinecap="round" />
    </svg>
  );
}

function SlackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[color:var(--color-abyssal)]/60">
      <path d="M14.5 2a2.5 2.5 0 0 0 0 5H17V4.5A2.5 2.5 0 0 0 14.5 2z" fill="currentColor" opacity=".4"/>
      <path d="M14.5 7H7v2.5h7.5V7z" fill="currentColor" opacity=".6"/>
      <path d="M22 9.5A2.5 2.5 0 0 0 17 9.5V12h2.5A2.5 2.5 0 0 0 22 9.5z" fill="currentColor" opacity=".4"/>
      <path d="M17 9.5V17h2.5V9.5H17z" fill="currentColor" opacity=".6"/>
      <path d="M9.5 22a2.5 2.5 0 0 0 0-5H7v2.5A2.5 2.5 0 0 0 9.5 22z" fill="currentColor" opacity=".4"/>
      <path d="M9.5 17H17v-2.5H9.5V17z" fill="currentColor" opacity=".6"/>
      <path d="M2 14.5A2.5 2.5 0 0 0 7 14.5V12H4.5A2.5 2.5 0 0 0 2 14.5z" fill="currentColor" opacity=".4"/>
      <path d="M7 14.5V7H4.5v7.5H7z" fill="currentColor" opacity=".6"/>
    </svg>
  );
}

function GoogleDocsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[color:var(--color-abyssal)]/60">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components (same as step 1)
// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16">
      <div className="mb-8 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[color:var(--color-north-star)]">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 4h8M3 7h5M3 10h6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <span className="font-semibold text-[color:var(--color-abyssal)]">Jotty</span>
      </div>
      {children}
    </main>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all ${
              i + 1 === current
                ? "w-6 bg-[color:var(--color-north-star)]"
                : i + 1 < current
                  ? "w-4 bg-[color:var(--color-north-star)]/40"
                  : "w-4 bg-[color:var(--color-abyssal)]/10"
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-[color:var(--color-abyssal)]/50">
        Step {current} of {total}
      </span>
    </div>
  );
}

function Spinner({ small }: { small?: boolean }) {
  const size = small ? 12 : 20;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin" aria-hidden>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
    </svg>
  );
}